import {
  valueForHostingWithCloudComplianceBoundaries,
  valueForPaymentModel,
} from '@atlassian/mpac-app-listing';
import {
  type AppListingCloudMigration,
  type HostingWithCloudComplianceBoundariesType,
  HostingTypesWithCloudComplianceBoundaries,
} from '@atlassian/mpac-types';
import {
  AtlassianProductHostingType,
  MarketplaceAppPaymentModel,
} from '@atlassian/mpac-types/graphql-gateway-types';

import type {
  MarketplaceAppByKeyQuery,
  PublicPrivateCloudVersions,
  PublicPrivateDataCenterVersions,
  PublicPrivateServerVersions,
} from '@atlassian/mpac-app-listing';

/**
 * Validates that an app version exists for the supplied hosting type.
 * If one does not exist, it attempts to pick the next available hosting
 * type for which the app has a version.
 */
export function getFirstValidHostingOption(
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>
): HostingWithCloudComplianceBoundariesType {
  if (appDetails.cloudAppVersions.edges?.[0]?.node) {
    return AtlassianProductHostingType.Cloud;
  }

  if (appDetails.dataCenterAppVersions.edges?.[0]?.node) {
    return AtlassianProductHostingType.DataCenter;
  }

  if (appDetails.serverAppVersions.edges?.[0]?.node) {
    return AtlassianProductHostingType.Server;
  }

  // Fallback to cloud if we can't find any matches
  return AtlassianProductHostingType.Cloud;
}

export type PublicAppVersion =
  | PublicPrivateCloudVersions['publicCloudVersion']
  | PublicPrivateServerVersions['publicServerVersion']
  | PublicPrivateDataCenterVersions['publicDataCenterVersion'];

export type PrivateAppVersion =
  | PublicPrivateCloudVersions['privateCloudVersion']
  | PublicPrivateServerVersions['privateServerVersion']
  | PublicPrivateDataCenterVersions['privateDataCenterVersion'];

export const hasNewerPrivateVersion = (
  privateVersion: PublicAppVersion,
  publicVersion: PrivateAppVersion
): boolean => {
  const privateBuildNumber: string | null | undefined =
    privateVersion.edges?.[0]?.node?.buildNumber;
  const publicBuildNumber: string | null | undefined = publicVersion.edges?.[0]?.node?.buildNumber;
  if (privateBuildNumber && publicBuildNumber == null) return true;
  if (publicBuildNumber == null || privateBuildNumber == null) return false;
  return Number.parseInt(privateBuildNumber) > Number.parseInt(publicBuildNumber);
};

export const toAnalyticsHosting = (hosting: HostingWithCloudComplianceBoundariesType) => {
  return valueForHostingWithCloudComplianceBoundaries(hosting)({
    [AtlassianProductHostingType.Cloud]: 'Cloud',
    [AtlassianProductHostingType.Server]: 'Server',
    [AtlassianProductHostingType.DataCenter]: 'Data Center',
    [HostingTypesWithCloudComplianceBoundaries.IsolatedCloud]: 'Isolated Cloud',
    [HostingTypesWithCloudComplianceBoundaries.GovernmentCloud]: 'Atlassian Government Cloud',
  });
};

export const toAnalyticsHostingForHostingSelector = (
  hosting: HostingWithCloudComplianceBoundariesType
) => {
  return valueForHostingWithCloudComplianceBoundaries(hosting)({
    [AtlassianProductHostingType.Cloud]: 'cloud',
    [AtlassianProductHostingType.Server]: 'server',
    [AtlassianProductHostingType.DataCenter]: 'Data Center',
    [HostingTypesWithCloudComplianceBoundaries.IsolatedCloud]: 'Isolated Cloud',
    [HostingTypesWithCloudComplianceBoundaries.GovernmentCloud]: 'Atlassian Government Cloud',
  });
};

export const toAnalyticsPaymentModel = (paymentModel: MarketplaceAppPaymentModel) => {
  return valueForPaymentModel(paymentModel)({
    [MarketplaceAppPaymentModel.Free]: 'Free',
    [MarketplaceAppPaymentModel.PaidViaAtlassian]: 'Paid via Atlassian',
    [MarketplaceAppPaymentModel.PaidViaPartner]: 'Paid via Vendor',
  });
};

export const getMigrationAssistanceStatus = (cloudMigration: AppListingCloudMigration) => {
  if (
    !(
      cloudMigration.alternateCloudAppKey ||
      cloudMigration.featureDifferenceDocumentation ||
      cloudMigration.migrationDocumentation
    )
  ) {
    return 'noCloud';
  } else if (cloudMigration.alternateCloudAppKey) {
    return 'cloudUnlinked';
  } else return 'cloudLinked';
};

export const getMigrationInformationAvailable = (cloudMigration: AppListingCloudMigration) => {
  const migrationInformationAvailable: Array<any | string> = [];
  if (cloudMigration.alternateCloudAppKey) {
    migrationInformationAvailable.push('cloudVariantLink');
  }
  if (cloudMigration.featureDifferenceDocumentation) {
    migrationInformationAvailable.push('migrationDifferences');
  }
  if (cloudMigration.migrationDocumentation) {
    migrationInformationAvailable.push('migrationPathway');
  }

  return migrationInformationAvailable;
};
